import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Play } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AuthLayout } from "../components/layout/AuthLayout";

// Login Page
export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Connexion réussie !");
      navigate(data.user.onboarding_complete ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    }
    setLoading(false);
  };

  return (
    <AuthLayout>
      <div className="animate-slide-up">
        <h2 className="text-3xl font-bold mb-2" style={{color: '#210875'}}>Connexion</h2>
        <p className="text-muted-foreground mb-8">Accède à ton espace Biz360 <span className="text-red-500">❤️‍🔥</span></p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" placeholder="email@exemple.com" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Mot de passe</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"} 
                data-testid="login-password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 px-4 pr-12 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" 
                placeholder="••••••••" 
                required 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <div className="text-right">
            <button type="button" onClick={() => navigate("/reset-password")} className="text-sm text-primary hover:underline" data-testid="forgot-password">
              Mot de passe oublié ?
            </button>
          </div>
          <button type="submit" data-testid="login-submit" disabled={loading}
            className="w-full py-4 rounded-full font-bold transition-all flex items-center justify-center gap-2 hover:shadow-xl disabled:opacity-50"
            style={{
              background: '#210875',
              color: '#F5E9D2',
              boxShadow: '0 6px 20px -6px rgba(33, 8, 117, 0.45)',
              letterSpacing: '0.01em',
            }}>
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>
        <p className="mt-6 text-center text-muted-foreground">
          Pas encore de compte ? <button onClick={() => navigate("/subscription")} className="text-primary font-medium hover:underline" data-testid="goto-register">Créer un compte</button>
        </p>

        {/* Bouton démo — aux couleurs Biz360 (pilule violette, texte crème) */}
        <div className="mt-6 pt-6 border-t border-dashed">
          <button
            type="button"
            data-testid="demo-access-btn"
            disabled={demoLoading}
            onClick={async () => {
              setDemoLoading(true);
              try {
                await login("demo@biz360.fr", "demo2026");
                toast.success("Bienvenue en mode démo !");
                navigate("/dashboard");
              } catch (err) {
                toast.error("Erreur d'accès à la démo");
              }
              setDemoLoading(false);
            }}
            className="group w-full py-4 rounded-full font-bold transition-all flex items-center justify-center gap-3 hover:shadow-xl disabled:opacity-70"
            style={{
              background: '#897FCB',
              color: '#F5E9D2',
              boxShadow: '0 6px 20px -6px rgba(33, 8, 117, 0.35)',
              letterSpacing: '0.01em',
            }}
          >
            <Play size={18} fill="#F5E9D2" stroke="#F5E9D2" className="transition-transform group-hover:translate-x-0.5" />
            {demoLoading ? "Connexion..." : "Explorer la démo"}
            <span className="text-lg leading-none">🌶</span>
          </button>
          <p className="text-xs text-center text-muted-foreground mt-3">
            Découvre Biz360 avec des données fictives, sans inscription
          </p>
        </div>

        {/* ──────────── Bande conformité (Urssaf / DGCCRF) ──────────── */}
        <div
          className="mt-8 pt-5 border-t border-border/60 text-center"
          data-testid="login-compliance-footer"
        >
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 mb-3">
            Informations légales & ressources
          </p>
          <nav className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
            <button
              onClick={() => navigate("/mentions-legales-cgu")}
              className="hover:text-foreground hover:underline transition"
              data-testid="login-mentions-link"
            >
              Mentions légales
            </button>
            <span className="opacity-40">•</span>
            <button
              onClick={() => navigate("/mentions-legales-cgu#cgu")}
              className="hover:text-foreground hover:underline transition"
              data-testid="login-cgu-link"
            >
              CGU
            </button>
            <span className="opacity-40">•</span>
            <button
              onClick={() => navigate("/cgv")}
              className="hover:text-foreground hover:underline transition"
              data-testid="login-cgv-link"
            >
              CGV
            </button>
            <span className="opacity-40">•</span>
            <button
              onClick={() => navigate("/politique-confidentialite")}
              className="hover:text-foreground hover:underline transition"
              data-testid="login-privacy-link"
            >
              Confidentialité
            </button>
          </nav>
          <nav className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
            <button
              onClick={() => navigate("/statut-auto-entrepreneur")}
              className="hover:text-foreground hover:underline transition"
              data-testid="login-statut-link"
            >
              L'essentiel du statut
            </button>
            <span className="opacity-40">•</span>
            <button
              onClick={() => navigate("/statut-auto-entrepreneur#cotisations")}
              className="hover:text-foreground hover:underline transition"
              data-testid="login-cotisations-link"
            >
              Cotisations
            </button>
            <span className="opacity-40">•</span>
            <a
              href="https://www.autoentrepreneur.urssaf.fr/portail/accueil.html"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground hover:underline transition inline-flex items-center gap-0.5"
              data-testid="login-urssaf-official-link"
            >
              Site officiel Urssaf ↗
            </a>
          </nav>
          <p className="mt-3 text-[10px] text-muted-foreground/70">
            Biz360 — Édité par Tiphaine SAMBLAT (SIREN 913 345 682) · Service en langue française
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};
